/* /reset-password?token=xxx — finaliser la réinitialisation avec le token reçu.
 * Backend : POST /auth/password/reset { token, new_password }.
 */
import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, KeyRound, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  useSEO({
    title: "Réinitialiser le mot de passe — Noam",
    description: "Définissez un nouveau mot de passe pour votre compte Noam.",
    path: "/reset-password",
  });
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const tokenFromUrl = params.get("token") || "";
  const [token, setToken] = useState(tokenFromUrl);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const tooShort = pwd && pwd.length < 8;
  const mismatch = pwd && pwd2 && pwd !== pwd2;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (tooShort) { setError(t("auth.reset_too_short", { defaultValue: "Le mot de passe doit contenir au moins 8 caractères." })); return; }
    if (mismatch) { setError(t("auth.reset_mismatch", { defaultValue: "Les deux mots de passe ne correspondent pas." })); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password/reset`, { token: token.trim(), new_password: pwd }, { withCredentials: true });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d && d.msg) ? d.msg : (err.message || "Erreur."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[var(--noam-bg)] text-white" data-testid="reset-root">
      <div className="absolute inset-0 noam-grid-bg opacity-30 pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] noam-glow pointer-events-none" />

      <header className="relative px-6 pt-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-[#378ADD]/20 border border-[#378ADD]/40 flex items-center justify-center">
            <span className="noam-display font-bold text-[#378ADD]">N</span>
          </div>
          <div className="leading-tight">
            <div className="noam-display text-base font-semibold">Noam</div>
            <div className="text-[10px] text-zinc-500 -mt-0.5">by Constante Partners</div>
          </div>
        </Link>
        <LanguageSwitcher compact />
      </header>

      <main className="relative flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="noam-card p-8">
            <div className="text-[#378ADD] text-[11px] uppercase tracking-[0.18em] font-semibold">
              {t("auth.reset_overline", { defaultValue: "Nouveau mot de passe" })}
            </div>
            <h1 className="noam-display text-2xl font-bold mt-2">
              {t("auth.reset_title", { defaultValue: "Définir un nouveau mot de passe" })}
            </h1>
            <p className="text-zinc-400 text-sm mt-2 leading-relaxed">
              {t("auth.reset_subtitle", { defaultValue: "Choisissez un mot de passe d'au moins 8 caractères." })}
            </p>

            {done ? (
              <div className="mt-7 space-y-3" data-testid="reset-done">
                <div className="flex items-start gap-2 p-4 rounded-md border border-[#1D9E75]/30 bg-[#1D9E75]/10 text-[#86EFAC] text-sm">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{t("auth.reset_done", { defaultValue: "Mot de passe mis à jour. Redirection vers la connexion…" })}</span>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-7 space-y-4" data-testid="reset-form">
                {!tokenFromUrl && (
                  <div>
                    <label className="text-[11px] text-zinc-400 uppercase tracking-wider font-medium mb-1.5 block">
                      {t("auth.reset_token_label", { defaultValue: "Code de réinitialisation" })}
                    </label>
                    <input
                      type="text"
                      required
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      className="noam-input font-mono text-xs"
                      placeholder="abc123…"
                      data-testid="reset-token-input"
                    />
                  </div>
                )}
                <div>
                  <label className="text-[11px] text-zinc-400 uppercase tracking-wider font-medium mb-1.5 block">
                    {t("auth.reset_new_password", { defaultValue: "Nouveau mot de passe" })}
                  </label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    className="noam-input"
                    placeholder="••••••••"
                    data-testid="reset-pwd-input"
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-zinc-400 uppercase tracking-wider font-medium mb-1.5 block">
                    {t("auth.reset_confirm_password", { defaultValue: "Confirmer le mot de passe" })}
                  </label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={pwd2}
                    onChange={(e) => setPwd2(e.target.value)}
                    className="noam-input"
                    placeholder="••••••••"
                    data-testid="reset-pwd2-input"
                    autoComplete="new-password"
                  />
                  {mismatch && (
                    <div className="text-[11px] text-[#FCA5A5] mt-1">{t("auth.reset_mismatch", { defaultValue: "Les deux mots de passe ne correspondent pas." })}</div>
                  )}
                </div>

                {error && (
                  <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs" data-testid="reset-error">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !token || !pwd || !pwd2 || tooShort || mismatch}
                  className="btn-primary w-full justify-center disabled:opacity-50"
                  data-testid="reset-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> {t("auth.reset_submitting", { defaultValue: "Mise à jour…" })}</>
                  ) : (
                    <><KeyRound className="w-4 h-4" /> {t("auth.reset_submit", { defaultValue: "Valider le nouveau mot de passe" })}</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
